import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { createCheckoutSession } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Scale, CreditCard, Check, LogOut, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function SubscribePage() {
  const navigate = useNavigate();
  const { user, logout, isSubscribed } = useAuth();
  const [loading, setLoading] = useState(false);

  // If already subscribed, redirect to dashboard
  if (isSubscribed()) {
    navigate('/dashboard');
    return null;
  }

  const handleSubscribe = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      const res = await createCheckoutSession(window.location.origin);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start payment');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const features = [
    'Upload thousands of documents (PDF, Word, images)',
    'AI-powered exhibit identification with GPT-5.2',
    'Automatic text extraction and OCR',
    'Custom exhibit labeling (Plaintiff, Defendant, etc.)',
    'Batch operations for multiple documents',
    'Case collaboration with team members',
    'Generate court-ready PDF reports',
    'Search across all documents',
    'Secure cloud storage',
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="subscribe-page">
      <Card className="w-full max-w-lg shadow-lg">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-md bg-navy flex items-center justify-center">
              <Scale className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-xl font-bold tracking-tight text-foreground">Legal AIDynamics</span>
          </div>
          <CardTitle className="font-heading text-2xl">Subscription Required</CardTitle>
          <CardDescription className="font-body">
            {user ? `Welcome, ${user.name}! ` : ''}Complete your subscription to access all features.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Pricing */}
          <div className="text-center py-4 bg-gradient-to-br from-navy/5 to-bronze/5 rounded-lg">
            <div className="text-4xl font-bold text-navy">$20</div>
            <div className="text-muted-foreground">per month</div>
          </div>

          {/* Features */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-foreground">Everything you need for trial preparation:</h4>
            <ul className="space-y-2">
              {features.map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <div className="space-y-3 pt-4">
            <Button 
              onClick={handleSubscribe} 
              disabled={loading}
              className="w-full bg-bronze hover:bg-bronze-light h-12 text-base"
              data-testid="subscribe-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processing...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Subscribe Now
                </span>
              )}
            </Button>
            
            <Button 
              variant="ghost" 
              onClick={handleLogout}
              className="w-full text-muted-foreground hover:text-foreground"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign out and use a different account
            </Button>
          </div>

          <p className="text-xs text-center text-muted-foreground">
            Secure payment powered by Stripe. Cancel anytime.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
